/**
 * Courier API Service
 * Handles all communication with the Courier worker API
 */

import accountService from './accountService';

const API_BASE = '/api';

class CourierApi {
  async request(path, options = {}) {
    const url = `${API_BASE}${path}`;
    const response = await accountService.authenticatedFetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  }

  // Provider Profiles
  async getMyProviderProfile() {
    return this.request('/providers/me');
  }

  async createProviderProfile(profile) {
    return this.request('/providers', {
      method: 'POST',
      body: JSON.stringify(profile),
    });
  }

  async updateProviderProfile(updates) {
    return this.request('/providers/me', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async getProvider(id) {
    return this.request(`/providers/${id}`);
  }

  async listProviders(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/providers${query ? `?${query}` : ''}`);
  }

  // Transport Requests
  async createTransportRequest(requestData) {
    return this.request('/requests', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  }

  async listRequests(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/requests${query ? `?${query}` : ''}`);
  }

  async getRequest(id) {
    return this.request(`/requests/${id}`);
  }

  async updateRequest(id, updates) {
    return this.request(`/requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async cancelRequest(id) {
    return this.request(`/requests/${id}`, {
      method: 'DELETE',
    });
  }

  // Offers
  async createOffer(offerData) {
    return this.request('/offers', {
      method: 'POST',
      body: JSON.stringify(offerData),
    });
  }

  async listOffers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/offers${query ? `?${query}` : ''}`);
  }

  async acceptOffer(offerId, data = {}) {
    return this.request(`/offers/${offerId}/accept`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async rejectOffer(offerId) {
    return this.request(`/offers/${offerId}/reject`, {
      method: 'PUT',
    });
  }

  // Contracts
  async listContracts(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/contracts${query ? `?${query}` : ''}`);
  }

  async getContract(id) {
    return this.request(`/contracts/${id}`);
  }

  async updateContractStatus(id, status) {
    return this.request(`/contracts/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async confirmDelivery(id) {
    return this.request(`/contracts/${id}/confirm`, {
      method: 'PUT',
    });
  }

  // Location tracking
  async sendLocationUpdate(contractId, location) {
    return this.request('/locations', {
      method: 'POST',
      body: JSON.stringify({ contractId, ...location }),
    });
  }

  async getLatestLocation(contractId) {
    return this.request(`/locations/${contractId}`);
  }

  async getLocationHistory(contractId, limit = 100) {
    return this.request(`/locations/${contractId}/history?limit=${limit}`);
  }

  // Reviews
  async createReview(reviewData) {
    return this.request('/reviews', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  async getReviews(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/reviews${query ? `?${query}` : ''}`);
  }

  async replyToReview(reviewId, replyText) {
    return this.request(`/reviews/${reviewId}/reply`, {
      method: 'PUT',
      body: JSON.stringify({ replyText }),
    });
  }

  // Notifications
  async getNotifications(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/notifications${query ? `?${query}` : ''}`);
  }

  async markNotificationRead(id) {
    return this.request(`/notifications/${id}/read`, {
      method: 'PUT',
    });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/read-all', {
      method: 'PUT',
    });
  }
}

export default new CourierApi();
